/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';

/* Import types */
import type { DeviceSensorCsvMetadataGroup } from '@/types/deviceSensor';

export type SystemInformationSensorKey = 'gas' | 'gridFeed' | 'gridSupply' | 'heat' | 'photovoltaics' | 'water';

export interface SystemInformationSensor {
    isEnabled: boolean;
    urn: string;
}

export interface SystemInformationAnomalySensorCount {
    anomaly: string;
    sensorCount: number;
}

export interface SystemInformation {
    address: string | null;
    anomalySensorCounts: SystemInformationAnomalySensorCount[];
    description: string | null;
    sensors: Record<SystemInformationSensorKey, SystemInformationSensor[]>;
    system: string;
}

export const SYSTEM_INFORMATION_SENSOR_FIELDS: Record<
    DeviceSensorCsvMetadataGroup,
    SystemInformationSensorKey
> = {
    BL: 'gridSupply',
    GAS: 'gas',
    GRID_FEED: 'gridFeed',
    HE: 'heat',
    PV: 'photovoltaics',
    WATER: 'water',
};

function createEmptySensorInformation(): Record<SystemInformationSensorKey, SystemInformationSensor[]> {
    return {
        gas: [],
        gridFeed: [],
        gridSupply: [],
        heat: [],
        photovoltaics: [],
        water: [],
    };
}

function getSensorSuffix(deviceName: string, group: DeviceSensorCsvMetadataGroup): string {
    if (!deviceName.startsWith('Mue.')) {
        return '';
    }

    return group === 'GAS' || group === 'WATER' ? '.V' : '.W';
}

function getAnomalySensorCounts(
    sensors: Record<SystemInformationSensorKey, SystemInformationSensor[]>,
): SystemInformationAnomalySensorCount[] {
    const getEnabledSensorCount = (sensorList: SystemInformationSensor[]): number => {
        return sensorList.filter((sensor) => sensor.isEnabled).length;
    };

    return [
        { anomaly: 'bl', sensorCount: getEnabledSensorCount(sensors.gridSupply) },
        { anomaly: 'grid_feed', sensorCount: getEnabledSensorCount(sensors.gridFeed) },
        { anomaly: 'pv', sensorCount: getEnabledSensorCount(sensors.photovoltaics) },
        { anomaly: 'heating_energy', sensorCount: getEnabledSensorCount(sensors.heat) },
        { anomaly: 'gas_bl', sensorCount: getEnabledSensorCount(sensors.gas) },
        { anomaly: 'water_bl', sensorCount: getEnabledSensorCount(sensors.water) },
    ].filter(({ sensorCount }) => sensorCount > 0);
}

/** Returns a system's CSV metadata and all configured supply sensors for display. */
export function getSystemInformation(
    deviceSensorCatalog: DeviceSensorCatalog,
    system: string,
): SystemInformation {
    const devices = deviceSensorCatalog.getDevicesBySystem(system);
    const firstDevice = devices[0];
    const metadata = firstDevice
        ? deviceSensorCatalog.getSensorMetadata(firstDevice)
        : null;
    const sensors = devices.reduce<Record<SystemInformationSensorKey, SystemInformationSensor[]>>(
        (systemSensors, deviceName) => {
            deviceSensorCatalog.getDeviceMetadataGroups(deviceName).forEach((group) => {
                const field = SYSTEM_INFORMATION_SENSOR_FIELDS[group];

                systemSensors[field].push({
                    isEnabled: deviceSensorCatalog.isMetadataGroupEnabled(deviceName, group),
                    urn: `${deviceName}${getSensorSuffix(deviceName, group)}`,
                });
            });

            return systemSensors;
        },
        createEmptySensorInformation(),
    );

    return {
        address: metadata?.address ?? null,
        anomalySensorCounts: getAnomalySensorCounts(sensors),
        description: metadata?.name ?? null,
        sensors,
        system,
    };
}
